import { useState, useRef, useCallback } from 'react';
import { Activity } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/app/components/ui/card';
import { ActivityTimeline } from '@/app/components/activity-timeline';
import { useProjectActivity } from '@/hooks/use-projects';
import type { ProjectActivityEvent } from '@/lib/types';

export function ProjectActivityTab({ projectId }: { projectId: number }) {
  const [page, setPage] = useState(1);
  const [allEvents, setAllEvents] = useState<ProjectActivityEvent[]>([]);

  const { data, isFetching } = useProjectActivity(projectId, page);

  const events =
    page === 1
      ? data?.results ?? []
      : [...allEvents, ...(data?.results ?? [])];

  const seen = new Set<string>();
  const dedupedEvents = events.filter((e) => {
    const key = `${e.type}-${e.timestamp}-${e.headline}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const hasMore = (data?.page ?? 1) < (data?.total_pages ?? 1);

  const allEventsRef = useRef(dedupedEvents);
  // eslint-disable-next-line react-hooks/refs
  allEventsRef.current = dedupedEvents;

  const handleLoadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      setAllEvents(allEventsRef.current);
      setPage((p) => p + 1);
    }
  }, [hasMore, isFetching]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-5 w-5 text-blue-600" />
          Project Activity
        </CardTitle>
        {data && (
          <p className="text-sm text-gray-500">
            {data.total_count} event{data.total_count !== 1 ? 's' : ''}
          </p>
        )}
      </CardHeader>
      <CardContent>
        <ActivityTimeline
          events={dedupedEvents}
          hasMore={hasMore}
          isLoadingMore={isFetching && page > 1}
          onLoadMore={handleLoadMore}
        />
      </CardContent>
    </Card>
  );
}
